import axiosInstance from "@/api/axiosInstance";
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment/moment";

import { ENDPOINTS } from "@/constants/api";
import { EventUser } from "@/common/model/User";
import ConfirmationUserDialog from "./ConfirmUserDialog";
import Overlay from "./OverlayGeneric";
import styles from "./Users.module.css";
import { Button, Input, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui";

// Define tab options
type TabOption = "users" | "devices";

interface UsersListProps {
  initialTab?: TabOption;
}

const UsersList: React.FC<UsersListProps> = ({ initialTab = "users" }) => {
  const [email, setEmail] = useState("");
  const [userIdForDelete, setUserIdForDelete] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventId, setEventId] = useState(0);
  const [editedUsers, setEditedUsers] = useState<EventUser[]>([]);
  const [showFlagOverlay, setShowFlagOverlay] = useState(false);
  const [newFlag, setNewFlag] = useState("");
  const [currentUser, setCurrentUser] = useState<EventUser>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<TabOption>(initialTab);
  
  // Device management states
  const [deviceUsers, setDeviceUsers] = useState<EventUser[]>([]);
  const [regularUsers, setRegularUsers] = useState<EventUser[]>([]);
  const [newDeviceEmail, setNewDeviceEmail] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isConvertDialogOpen, setIsConvertDialogOpen] = useState(false);
  const [isAddDeviceDialogOpen, setIsAddDeviceDialogOpen] = useState(false);

  // Sorting states for devices
  type DeviceSortField = 'name' | 'email' | 'lastping';
  type SortDirection = 'asc' | 'desc';
  const [deviceSortField, setDeviceSortField] = useState<DeviceSortField>(() => {
    const saved = localStorage.getItem('deviceSortField');
    return (saved as DeviceSortField) || 'email';
  });
  const [deviceSortDirection, setDeviceSortDirection] = useState<SortDirection>(() => {
    const saved = localStorage.getItem('deviceSortDirection');
    return (saved as SortDirection) || 'asc';
  });

  // Sorting states for users
  type UserSortField = 'id' | 'name' | 'alias' | 'localid' | 'lastping';
  const [userSortField, setUserSortField] = useState<UserSortField>(() => {
    const saved = localStorage.getItem('userSortField');
    return (saved as UserSortField) || 'id';
  });
  const [userSortDirection, setUserSortDirection] = useState<SortDirection>(() => {
    const saved = localStorage.getItem('userSortDirection');
    return (saved as SortDirection) || 'asc';
  });
  
  // Handle sorting for devices
  const handleDeviceSort = (field: DeviceSortField) => {
    const newDirection = deviceSortField === field && deviceSortDirection === 'asc' ? 'desc' : 'asc';
    setDeviceSortField(field);
    setDeviceSortDirection(newDirection);
    localStorage.setItem('deviceSortField', field);
    localStorage.setItem('deviceSortDirection', newDirection);
  };

  // Handle sorting for users
  const handleUserSort = (field: UserSortField) => {
    const newDirection = userSortField === field && userSortDirection === 'asc' ? 'desc' : 'asc';
    setUserSortField(field);
    setUserSortDirection(newDirection);
    localStorage.setItem('userSortField', field);
    localStorage.setItem('userSortDirection', newDirection);
  };

  // Sort devices
  const sortedDeviceUsers = [...deviceUsers].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (deviceSortField) {
      case 'name':
        aValue = (a.displayname || a.user?.name || '');
        bValue = (b.displayname || b.user?.name || '');
        // Use natural sort for names with numbers
        const nameCompare = aValue.localeCompare(bValue, undefined, {
          numeric: true,
          sensitivity: 'base'
        });
        return deviceSortDirection === 'asc' ? nameCompare : -nameCompare;
      case 'email':
        aValue = (a.user?.username || '').toLowerCase();
        bValue = (b.user?.username || '').toLowerCase();
        break;
      case 'lastping':
        aValue = a.lastping ? new Date(a.lastping as any).getTime() : 0;
        bValue = b.lastping ? new Date(b.lastping as any).getTime() : 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return deviceSortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return deviceSortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Sort users (regular users, not devices)
  const sortedRegularUsers = [...editedUsers]
    .filter(user => !user.issource)
    .sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (userSortField) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'name':
          aValue = (a.user?.name || '');
          bValue = (b.user?.name || '');
          // Use natural sort for names with numbers
          const nameCompare = aValue.localeCompare(bValue, undefined, {
            numeric: true,
            sensitivity: 'base'
          });
          return userSortDirection === 'asc' ? nameCompare : -nameCompare;
        case 'alias':
          aValue = (a.displayname || '');
          bValue = (b.displayname || '');
          // Use natural sort for aliases with numbers
          const aliasCompare = aValue.localeCompare(bValue, undefined, {
            numeric: true,
            sensitivity: 'base'
          });
          return userSortDirection === 'asc' ? aliasCompare : -aliasCompare;
        case 'localid':
          aValue = (a.localid || '');
          bValue = (b.localid || '');
          // Use natural sort for local IDs with numbers
          const localidCompare = aValue.localeCompare(bValue, undefined, {
            numeric: true,
            sensitivity: 'base'
          });
          return userSortDirection === 'asc' ? localidCompare : -localidCompare;
        case 'lastping':
          aValue = a.lastping ? new Date(a.lastping as any).getTime() : 0;
          bValue = b.lastping ? new Date(b.lastping as any).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return userSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return userSortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const handleDeviceNameChange = async (userId: number, newName: string) => {
    // First update the local state
    const updatedDeviceUsers = deviceUsers.map(user => 
      user.id === userId ? { ...user, displayname: newName } : user
    );
    setDeviceUsers(updatedDeviceUsers);
    
    // Also update in the main users list
    const updatedUsers = editedUsers.map(user => 
      user.id === userId ? { ...user, displayname: newName } : user
    );
    setEditedUsers(updatedUsers);
    
    // Save the change to the backend
    try {
      // We'll use the same endpoint that's used for saving alias changes
      await axiosInstance.put(
        `${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}/user/alias`,
        [{ id: userId, displayname: newName }]
      );
    } catch (error) {
      console.error("Error updating device name:", error);
      // Revert the change in case of error
      loadUsers(eventId);
    }
  };

  const handleFlagClick = (user: EventUser) => {
    setShowFlagOverlay(true);
    setCurrentUser(user);
  };

  const handleFlagSubmit = () => {
    if (currentUser && newFlag) {
      currentUser.countryFlag = newFlag.toLowerCase();
      setShowFlagOverlay(false);
      setNewFlag("");
    }
  };

  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }
  const query = useQuery();
  const navigate = useNavigate();

  const handleProfilePicClick = (user: EventUser) => {
    setCurrentUser(user);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await fetch(
          `${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}/user/${currentUser?.user.id}/profile/image`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (currentUser !== undefined) {
            currentUser.profilepicid = data.pictureid;
            setCurrentUser(currentUser);
          }
        } else {
          console.error("Upload failed");
        }
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    }
  };

  function loadUsers(id: number) {
    axiosInstance
      .get(`${ENDPOINTS.COMPETITIONS.DEFAULT}/${id}/user`)
      .then((response) => {
        const users = response.data;
        setEditedUsers(users);
        
        // Filter users into device users and regular users
        setDeviceUsers(users.filter((user: EventUser) => user.issource));
        setRegularUsers(users.filter((user: EventUser) => !user.issource));
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
      });
  }

  useEffect(() => {
    // Set the active tab based on the initialTab prop
    if (initialTab) {
      setActiveTab(initialTab);
    }
    
    const id = query.get("id");
    console.log("UserList id: " + id);
    if (id) {
      setEventId(Number(id));
      // Fetch heats and users from the backend
      loadUsers(Number(id));
      // loadCompetition(Number(id));
    } else {
      const storedId = sessionStorage.getItem("competitionId");
      if (storedId) {
        console.log("UserList id from session: " + storedId);
        setEventId(Number(storedId));
        loadUsers(Number(storedId));
      } else {
        console.log("No competition ID found");
        // Redirect to competitions list
        navigate('/competitions');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAliasChange = (userId: number, newAlias: string) => {
    const updatedUsers = editedUsers.map((user) =>
      user.id === userId ? { ...user, displayname: newAlias } : user
    );
    setEditedUsers(updatedUsers);
  };

  const handleLocalidChange = (userId: number, newLocalId: string) => {
    const updatedUsers = editedUsers.map((user) =>
      user.id === userId ? { ...user, localid: newLocalId } : user
    );
    setEditedUsers(updatedUsers);
  };

  function removeUserFromEvent(userId: number) {
    axiosInstance
      .delete(`${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}/user/${userId}`)
      .then((response) => {
        loadUsers(eventId);
      })
      .catch((error) => {
        alert("error removing user: " + error);
        console.error("Error removing user:", error);
      });
  }

  const handleSave = () => {
    onSave(editedUsers);
  };
  const fakeJump = (userId: number) => {
    axiosInstance
      .get(`${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}/${userId}/fakejump`)
      .then((response) => {
        alert("Fake jump send!");
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
      });
  };
  const buzzer = (userId: number) => {
    axiosInstance
      .get(`${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}/${userId}/buzzer`)
      .then((response) => {
        alert("Buzzer send!");
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
      });
  };
  function onSave(editedUsers: EventUser[]) {
    // Clean the data to avoid backend deserialization issues
    // Only send the fields that the backend needs
    const cleanedUsers = editedUsers.map(user => ({
      id: user.id,
      displayname: user.displayname,
      localid: user.localid,
      countryFlag: user.countryFlag,
      profilepicid: user.profilepicid,
      eventid: user.eventid || eventId,
      issource: user.issource
      // Don't send the nested user object to avoid deserialization issues
    }));
    
    axiosInstance
      .put(
        `${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}/user/alias`,
        cleanedUsers
      )
      .then((response) => {
        setEditedUsers(response.data);
      })
      .catch((error) => {
        console.error("Error updating user aliases:", error);
      });
  }

  function addUserToCompetition(id: number, email: string) {
    axiosInstance
      .post(
        `${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}/user/${email}`,
        editedUsers
      )
      .then((response) => {
        loadUsers(id);
        setEmail("");
      })
      .catch((error) => {
        alert("Error creating user: " + error);
        console.error("Error fetching users:", error);
      });
    return undefined;
  }
  
  // Device management functions
  const handleAddNewDevice = async () => {
    if (!newDeviceEmail) return;
    
    try {
      // First add the user normally
      const addResponse = await axiosInstance.post(
        `${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}/user/${newDeviceEmail}`
      );
      
      if (addResponse.status === 200 || addResponse.status === 201) {
        // Get the newly added user
        const newUser = addResponse.data;
        
        // Then convert the user to a device
        const convertResponse = await axiosInstance.put(
          `${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}/user/${newUser.id}/convert-to-device`,
          { isDevice: true },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (convertResponse.status === 200) {
          setNewDeviceEmail("");
          loadUsers(eventId);
        }
      }
    } catch (error) {
      console.error("Error adding device user:", error);
      alert("Error adding device user: " + error);
    }
  };

  const handleConvertToDevice = async (user: EventUser) => {
    try {
      const response = await axiosInstance.put(
        `${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}/user/${user.id}/convert-to-device`,
        { isDevice: true },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200) {
        loadUsers(eventId);
      }
    } catch (error) {
      console.error("Error converting user to device:", error);
      alert("Error converting user to device: " + error);
    }
  };

  const handleConvertToRegular = async (user: EventUser) => {
    try {
      const response = await axiosInstance.put(
        `${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}/user/${user.id}/convert-to-device`,
        { isDevice: false },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200) {
        loadUsers(eventId);
      }
    } catch (error) {
      console.error("Error converting device to regular user:", error);
      alert("Error converting device to regular user: " + error);
    }
  };

  const filteredRegularUsers = regularUsers.filter(user =>
    user.displayname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.user?.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Helper function to get ping status with color indicators
  function getPingStatus(lastping: Date | null | undefined | any): {
    color: string;
    bgColor: string;
    showCircle: boolean;
    displayText: string;
    minutesAgo: number | null;
  } {
    if (!lastping) {
      return {
        color: '#ef4444', // red
        bgColor: '#fee2e2', // red-100
        showCircle: true,
        displayText: 'Offline',
        minutesAgo: null
      };
    }

    const now = moment();
    const lastpingStr = String(lastping);
    let lastPingMoment: moment.Moment;

    if (typeof lastping === 'string') {
      lastPingMoment = moment.utc(lastpingStr.replace("[UTC]", "")).local();
    } else if (typeof lastping === 'number') {
      lastPingMoment = moment(lastping);
    } else {
      lastPingMoment = moment.utc(lastpingStr.replace("[UTC]", "")).local();
    }

    if (!lastPingMoment.isValid()) {
      return {
        color: '#ef4444',
        bgColor: '#fee2e2',
        showCircle: true,
        displayText: 'Invalid date',
        minutesAgo: null
      };
    }

    const minutesAgo = now.diff(lastPingMoment, 'minutes');
    const secondsAgo = now.diff(lastPingMoment, 'seconds');

    if (minutesAgo < 1) {
      // Green - online, recent ping
      return {
        color: '#10b981', // green-500
        bgColor: '#d1fae5', // green-100
        showCircle: true,
        displayText: `${secondsAgo} sec ago`,
        minutesAgo: 0
      };
    } else if (minutesAgo <= 3) {
      // Yellow - warning
      return {
        color: '#f59e0b', // amber-500
        bgColor: '#fef3c7', // amber-100
        showCircle: true,
        displayText: `${minutesAgo} min ago`,
        minutesAgo
      };
    } else {
      // Red - offline
      return {
        color: '#ef4444', // red-500
        bgColor: '#fee2e2', // red-100
        showCircle: true,
        displayText: `${minutesAgo} min ago`,
        minutesAgo
      };
    }
  }

  function getExactPingTime(lastping: Date | null | undefined | any): string {
    if (!lastping) {
      return 'Never';
    }

    const lastpingStr = String(lastping);
    let lastPingMoment: moment.Moment;

    if (typeof lastping === 'string') {
      lastPingMoment = moment.utc(lastpingStr.replace("[UTC]", "")).local();
    } else if (typeof lastping === 'number') {
      lastPingMoment = moment(lastping);
    } else {
      lastPingMoment = moment.utc(lastpingStr.replace("[UTC]", "")).local();
    }

    if (!lastPingMoment.isValid()) {
      return 'Invalid date';
    }

    return lastPingMoment.format("DD-MM HH:mm:ss");
  }

  return (
    <div className="flex gap-4 flex-col justify-between items-center min-h-[calc(100vh-7rem)]">
      <div className="w-full flex flex-col gap-4">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="flex -mb-px" aria-label="Tabs">
            <button
              onClick={() => setActiveTab("users")}
              className={`w-1/2 py-4 px-1 text-center border-b-2 font-medium text-base ${
                activeTab === "users"
                  ? "border-blue-600 text-blue-600 border-b-4"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <span className="text-lg">Users</span>
            </button>
            <button
              onClick={() => setActiveTab("devices")}
              className={`w-1/2 py-4 px-1 text-center border-b-2 font-medium text-base ${
                activeTab === "devices"
                  ? "border-blue-600 text-blue-600 border-b-4"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <span className="text-lg">Devices</span>
            </button>
          </nav>
        </div>

        {/* Users Tab Content */}
        {activeTab === "users" && (
          <>
            <div className="flex flex-wrap gap-4 items-center justify-between bg-gray-50 p-4 rounded-lg shadow-sm">
              <div className="flex-1 flex gap-2 items-center min-w-[300px]">
                <label className="whitespace-nowrap font-medium text-gray-700">Add User:</label>
                <Input
                  className="flex-1 bg-white border border-gray-300"
                  type="email"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Button 
                  onClick={(e) => addUserToCompetition(eventId, email)}
                  className="whitespace-nowrap"
                >
                  Add User
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="py-1 px-2 bg-gray-100 border-b border-gray-200 flex items-center">
                <h2 className="text-sm font-medium text-gray-700">Users List</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleUserSort('id')}
                      >
                        <div className="flex items-center gap-1">
                          ID
                          {userSortField === 'id' && (
                            <span className="text-blue-600">
                              {userSortDirection === 'asc' ? '▲' : '▼'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Picture</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Flag</th>
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleUserSort('name')}
                      >
                        <div className="flex items-center gap-1">
                          Name <span className="text-blue-500 ml-1">(editable)</span>
                          {userSortField === 'name' && (
                            <span className="text-blue-600">
                              {userSortDirection === 'asc' ? '▲' : '▼'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleUserSort('alias')}
                      >
                        <div className="flex items-center gap-1">
                          Alias
                          {userSortField === 'alias' && (
                            <span className="text-blue-600">
                              {userSortDirection === 'asc' ? '▲' : '▼'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleUserSort('localid')}
                      >
                        <div className="flex items-center gap-1">
                          Local ID
                          {userSortField === 'localid' && (
                            <span className="text-blue-600">
                              {userSortDirection === 'asc' ? '▲' : '▼'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleUserSort('lastping')}
                      >
                        <div className="flex items-center gap-1">
                          Last Ping
                          {userSortField === 'lastping' && (
                            <span className="text-blue-600">
                              {userSortDirection === 'asc' ? '▲' : '▼'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedRegularUsers.map((user) => {
                    let profilePicUrl = `${ENDPOINTS.IMAGES.PROFILE}/${user.user.profilepicid}`;
                    // let profilePicUrl = `http://kiter-271715.appspot.com/image/profile/${user.user.profilepicid}`;
                    if (user.profilepicid && user.profilepicid.length > 3) {
                      profilePicUrl = `${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}/user/${user.user.id}/profile/image/${user.profilepicid}`;
                    }
                    let countryFlag = user.countryFlag || user.user.country?.alpha2;
                    if (countryFlag !== undefined) {
                      countryFlag = countryFlag.toLowerCase();
                    }
                    const countryFlagUrl = `http://kiter-271715.appspot.com/flags/${countryFlag}.png`;

                    return (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{user.id}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex-shrink-0 h-10 w-10">
                            <img
                              onClick={() => handleProfilePicClick(user)}
                              src={profilePicUrl}
                              alt="Profile"
                              className="h-10 w-10 rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <img
                            onClick={(e) => handleFlagClick(user)}
                            src={countryFlagUrl}
                            alt="Flag"
                            className="h-5 w-8 object-cover cursor-pointer hover:opacity-80 transition-opacity"
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{user.user?.name || ""}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Input
                            type="text"
                            value={user.displayname || ""}
                            onChange={(e) => handleAliasChange(user.id, e.target.value)}
                            className="max-w-[150px] border-2 border-gray-300 hover:border-blue-400 focus:border-blue-500 bg-gray-50"
                            placeholder="Enter alias..."
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Input
                            disabled={user.issource}
                            type="text"
                            value={user.localid || ""}
                            onChange={(e) => handleLocalidChange(user.id, e.target.value)}
                            className="max-w-[100px] border-2 border-gray-300 hover:border-blue-400 focus:border-blue-500 bg-gray-50"
                            placeholder="Enter ID..."
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {(() => {
                            const status = getPingStatus(user.lastping);
                            const exactTime = getExactPingTime(user.lastping);
                            return (
                              <div className="flex items-center gap-2 cursor-help" title={`Last ping: ${exactTime}`}>
                                <div
                                  className="w-3 h-3 rounded-full flex-shrink-0 flex items-center justify-center"
                                  style={{ backgroundColor: status.bgColor }}
                                >
                                  <div
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{ backgroundColor: status.color }}
                                  ></div>
                                </div>
                                <span
                                  className="text-sm font-medium"
                                  style={{ color: status.color }}
                                >
                                  {status.displayText}
                                </span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex gap-2">
                            <div className="flex gap-1">
                              <Button size="sm" onClick={() => fakeJump(user.user?.id)}>
                                Fake Jump
                              </Button>
                              <Button size="sm" onClick={() => buzzer(user.user?.id)}>
                                Buzzer
                              </Button>
                            </div>
                            <Button
                              size="sm"
                              variant="delete"
                              onClick={() => {
                                setShowDeleteModal(true);
                                setUserIdForDelete(user.user?.id);
                              }}
                            >
                              Delete
                            </Button>
                            {userIdForDelete === user.user?.id && (
                              <ConfirmationUserDialog
                                open={showDeleteModal}
                                onClose={() => setShowDeleteModal(false)}
                                onConfirm={() => removeUserFromEvent(user.user.id)}
                                eventUser={user}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-center">
            <Button 
              onClick={handleSave}
              className="px-8 bg-green-600 hover:bg-green-700"
            >
              Save Changes
            </Button>
          </div>
        </div>
          </>
        )}

        {/* Devices Tab Content */}
        {activeTab === "devices" && (
          <div className="space-y-6">
            {/* Current Devices Section - Now at the top */}
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-blue-800">Current Devices</h3>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => setIsConvertDialogOpen(true)}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    Convert User to Device
                  </Button>
                  <Button 
                    onClick={() => setIsAddDeviceDialogOpen(true)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    Add New Device
                  </Button>
                </div>
              </div>
              
              {deviceUsers.length === 0 ? (
                <div className="bg-white p-6 rounded-lg border border-gray-200 text-center text-gray-500 italic">
                  No devices found. Add a device using the buttons above.
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleDeviceSort('name')}
                          >
                            <div className="flex items-center gap-1">
                              Name
                              {deviceSortField === 'name' && (
                                <span className="text-blue-600">
                                  {deviceSortDirection === 'asc' ? '▲' : '▼'}
                                </span>
                              )}
                            </div>
                          </th>
                          <th
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleDeviceSort('email')}
                          >
                            <div className="flex items-center gap-1">
                              Email
                              {deviceSortField === 'email' && (
                                <span className="text-blue-600">
                                  {deviceSortDirection === 'asc' ? '▲' : '▼'}
                                </span>
                              )}
                            </div>
                          </th>
                          <th
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleDeviceSort('lastping')}
                          >
                            <div className="flex items-center gap-1">
                              Last Ping
                              {deviceSortField === 'lastping' && (
                                <span className="text-blue-600">
                                  {deviceSortDirection === 'asc' ? '▲' : '▼'}
                                </span>
                              )}
                            </div>
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {sortedDeviceUsers.map(user => (
                          <tr key={user.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <Input
                                type="text"
                                value={user.displayname || ""}
                                onChange={(e) => handleDeviceNameChange(user.id, e.target.value)}
                                className="border-2 border-gray-300 hover:border-blue-400 focus:border-blue-500 bg-gray-50 w-full"
                                placeholder="Enter device name..."
                              />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {user.user?.username}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {(() => {
                                const status = getPingStatus(user.lastping);
                                const exactTime = getExactPingTime(user.lastping);
                                return (
                                  <div className="flex items-center gap-2 cursor-help" title={`Last ping: ${exactTime}`}>
                                    <div
                                      className="w-3 h-3 rounded-full flex-shrink-0 flex items-center justify-center"
                                      style={{ backgroundColor: status.bgColor }}
                                    >
                                      <div
                                        className="w-1.5 h-1.5 rounded-full"
                                        style={{ backgroundColor: status.color }}
                                      ></div>
                                    </div>
                                    <span
                                      className="text-sm font-medium"
                                      style={{ color: status.color }}
                                    >
                                      {status.displayText}
                                    </span>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              <div className="flex justify-end gap-2">
                                <Button 
                                  variant="secondary" 
                                  size="sm"
                                  onClick={() => handleConvertToRegular(user)}
                                  className="bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-300"
                                >
                                  Convert to User
                                </Button>
                                <Button
                                  size="sm"
                                  variant="delete"
                                  onClick={() => {
                                    setShowDeleteModal(true);
                                    setUserIdForDelete(user.user?.id);
                                  }}
                                >
                                  Delete
                                </Button>
                                {userIdForDelete === user.user?.id && (
                                  <ConfirmationUserDialog
                                    open={showDeleteModal}
                                    onClose={() => setShowDeleteModal(false)}
                                    onConfirm={() => removeUserFromEvent(user.user.id)}
                                    eventUser={user}
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            
          </div>
        )}
        
        <Overlay
          open={showFlagOverlay}
          onClose={() => setShowFlagOverlay(false)}
          title="Change Flag"
          onConfirm={handleFlagSubmit}
          confirmText="Update Flag"
        >
          <Input
            type="text"
            placeholder="Enter country code (e.g., us, de, fr)"
            value={newFlag}
            onChange={(e) => setNewFlag(e.target.value)}
            className="w-full"
          />
          <p className="text-sm text-gray-500 mt-2">
            Enter the two-letter country code to change the flag.
          </p>
        </Overlay>
        
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileUpload}
          accept="image/*"
        />
        <Overlay
          open={showFlagOverlay}
          onClose={() => setShowFlagOverlay(false)}
          title="Change Flag"
          onConfirm={handleFlagSubmit}
          confirmText="Update Flag"
        >
          <Input
            type="text"
            placeholder="Enter country code (e.g., us, de, fr)"
            value={newFlag}
            onChange={(e) => setNewFlag(e.target.value)}
            className="w-full"
          />
          <p className="text-sm text-gray-500 mt-2">
            Enter the two-letter country code to change the flag.
          </p>
        </Overlay>
        
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileUpload}
          accept="image/*"
        />
        
        {/* Add the Convert User to Device Dialog */}
        <Dialog open={isConvertDialogOpen} onOpenChange={setIsConvertDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-purple-800">Convert User to Device</DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Users
                </label>
                <Input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                />
              </div>
              
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden max-h-[400px] overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredRegularUsers.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-4 text-center text-sm text-gray-500 italic">
                          No users found matching your search
                        </td>
                      </tr>
                    ) : (
                      filteredRegularUsers.map(user => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {user.displayname || user.user?.name}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {user.user?.username}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <Button 
                              variant="secondary" 
                              size="sm"
                              onClick={() => {
                                handleConvertToDevice(user);
                                setIsConvertDialogOpen(false);
                              }}
                              className="bg-purple-100 text-purple-800 hover:bg-purple-200 border-purple-300"
                            >
                              Convert to Device
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                onClick={() => setIsConvertDialogOpen(false)}
                variant="secondary"
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Add New Device Dialog */}
        <Dialog open={isAddDeviceDialogOpen} onOpenChange={setIsAddDeviceDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-green-800">Add New Device</DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Device Email Address
                </label>
                <Input
                  type="email"
                  placeholder="Enter email address for new device"
                  value={newDeviceEmail}
                  onChange={(e) => setNewDeviceEmail(e.target.value)}
                  className="w-full"
                />
              </div>
              
              <p className="text-sm text-gray-500 mt-2">
                This will create a new user account that will be marked as a device (isSource = true).
              </p>
            </div>
            
            <DialogFooter>
              <Button 
                onClick={() => setIsAddDeviceDialogOpen(false)}
                variant="secondary"
                className="mr-2"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  handleAddNewDevice();
                  setIsAddDeviceDialogOpen(false);
                }}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Add Device
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default UsersList;
